import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, GraduationCap } from "lucide-react";

import { pageQuery, listQuery } from "@/lib/queries";
import { PageHero, Section, SectionTitle, DynamicIcon, EmptyState } from "@/components/site/bits";

export const Route = createFileRoute("/quem-e")({
  head: () => ({
    meta: [
      { title: "Quem é Rurdiney | Professor Rurdiney — 40123" },
      {
        name: "description",
        content:
          "Conheça a trajetória do Professor Rurdiney: da sala de aula à luta por uma cidade mais justa e educada. Nº 40123.",
      },
      { property: "og:title", content: "Quem é Rurdiney | Professor Rurdiney — 40123" },
      {
        property: "og:description",
        content: "A história de vida e a trajetória do Professor Rurdiney, candidato a vereador.",
      },
    ],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(pageQuery("quem-e")),
      context.queryClient.ensureQueryData(listQuery("timeline")),
      context.queryClient.ensureQueryData(listQuery("numbers")),
    ]),
  component: QuemEPage,
});

function QuemEPage() {
  const { data: page } = useSuspenseQuery(pageQuery("quem-e"));
  const { data: timeline } = useSuspenseQuery(listQuery("timeline"));
  const { data: numbers } = useSuspenseQuery(listQuery("numbers"));

  const blocks: Array<{ type: string; level?: number; text: string }> =
    (page?.content as any)?.blocks ?? [];

  return (
    <>
      <PageHero
        eyebrow="A história"
        title={page?.title || "Quem é Rurdiney"}
        description={page?.subtitle || "Uma trajetória de dedicação à educação e à comunidade."}
      />

      <Section>
        <div className="grid gap-12 md:grid-cols-[2fr_1fr]">
          <div className="prose-campaign">
            {blocks.length === 0 && <EmptyState message="Conteúdo em breve." />}
            {blocks.map((b, i) =>
              b.type === "heading" ? (
                <h2 key={i} className="mt-8 text-2xl md:text-3xl text-balance-title first:mt-0">
                  {b.text}
                </h2>
              ) : (
                <p key={i} className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {b.text}
                </p>
              ),
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <GraduationCap className="size-8 text-primary" />
              <h3 className="mt-3 font-display text-lg text-foreground">Em números</h3>
              <div className="mt-4 grid grid-cols-2 gap-4">
                {numbers.map((n: any) => (
                  <div key={n.id} className="rounded-xl bg-secondary/60 p-3 text-center">
                    <div className="font-display text-2xl text-accent-deep">
                      {n.value}
                      {n.suffix}
                    </div>
                    <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                      {n.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </Section>

      <Section className="bg-secondary/50">
        <SectionTitle
          eyebrow="Trajetória"
          title="Linha do tempo"
          description="Os marcos de uma vida dedicada à educação e ao serviço público."
        />
        <div className="relative mt-14 pl-8 md:pl-0">
          <div
            className="absolute left-[7px] top-0 bottom-0 w-0.5 bg-border md:left-1/2 md:-translate-x-1/2"
            aria-hidden="true"
          />
          <ol className="space-y-10">
            {timeline.map((t: any, i: number) => (
              <li
                key={t.id}
                className={`relative flex flex-col gap-3 md:flex-row md:items-center ${
                  i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                <span className="absolute left-0 top-1 flex size-4 items-center justify-center rounded-full bg-accent shadow-glow md:left-1/2 md:-translate-x-1/2">
                  <span className="size-2 rounded-full bg-accent-foreground" />
                </span>
                <div className="md:w-1/2" />
                <div
                  className={`rounded-2xl border border-border bg-card p-5 shadow-card md:w-1/2 ${
                    i % 2 === 0 ? "md:mr-6" : "md:ml-6"
                  }`}
                >
                  <div className="font-display text-xl text-accent-deep">{t.year}</div>
                  <div className="mt-1 text-sm font-semibold text-foreground">{t.title}</div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {t.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      <Section className="text-center">
        <Link
          to="/propostas"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-primary-deep"
        >
          Conheça as propostas <ArrowRight className="size-4" />
        </Link>
      </Section>
    </>
  );
}
